/** Ant Design theme mapped from shared design tokens. */
import type { ThemeConfig } from 'antd';
import { theme } from 'antd';

import { COLOR, FONT, RADIUS, SEMANTIC_COLOR, SPACE } from '@/shared/design-tokens/design-tokens';

const DESIGN_TOKENS = {
  colors: {
    primary: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
    primaryHover: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL2,
    primaryActive: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL2,
    surface: {
      level1: COLOR.GRAY[100],
      level2: COLOR.GRAY[98],
      level3: COLOR.GRAY[94],
    },
    onSurface: {
      level1: COLOR.GRAY[10],
      level2: COLOR.GRAY[20],
      level3: COLOR.GRAY[30],
    },
    primaryContainer: {
      main: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
      hover: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL2,
    },
    selectedNode: {
      background: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL2,
      text: SEMANTIC_COLOR.CONTAINER.PRIMARY.LEVEL1,
    },
    nodeHover: COLOR.GRAY[90],
  },
  typography: {
    fontFamily: FONT.FAMILY.NANUMSQUARE,
    fontSize: {
      xs: FONT.SIZE.XS,
      sm: FONT.SIZE.SM,
      base: FONT.SIZE.BASE,
      h1: FONT.SIZE['3XL'],
      h2: FONT.SIZE['2XL'],
      h3: FONT.SIZE.XL,
      h4: FONT.SIZE.LG,
      h5: FONT.SIZE.BASE,
    },
    lineHeight: FONT.LINE_HEIGHT[20] / FONT.SIZE.BASE,
    lineHeightTree: FONT.LINE_HEIGHT[20] / FONT.SIZE.SM,
  },
  spacing: {
    borderRadius: RADIUS[8],
    borderRadiusSmall: RADIUS[6],
    borderRadiusXSmall: RADIUS[4],
    padding: SPACE[8],
    paddingXS: SPACE[4],
    paddingLG: SPACE[12],
  },
} as const;

export const antdThemeConfig: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: DESIGN_TOKENS.colors.primary,
    colorBgContainer: DESIGN_TOKENS.colors.surface.level1,
    colorBgLayout: DESIGN_TOKENS.colors.surface.level2,
    colorText: DESIGN_TOKENS.colors.onSurface.level1,
    colorTextSecondary: DESIGN_TOKENS.colors.onSurface.level3,
    colorTextDisabled: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL7,
    colorBgContainerDisabled: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL3,

    fontFamily: DESIGN_TOKENS.typography.fontFamily,
    fontSize: DESIGN_TOKENS.typography.fontSize.base,
    fontSizeHeading1: DESIGN_TOKENS.typography.fontSize.h1,
    fontSizeHeading2: DESIGN_TOKENS.typography.fontSize.h2,
    fontSizeHeading3: DESIGN_TOKENS.typography.fontSize.h3,
    fontSizeHeading4: DESIGN_TOKENS.typography.fontSize.h4,
    fontSizeHeading5: DESIGN_TOKENS.typography.fontSize.h5,
    lineHeight: DESIGN_TOKENS.typography.lineHeight,

    borderRadius: DESIGN_TOKENS.spacing.borderRadius,
    boxShadowSecondary: '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
    colorBorderSecondary: COLOR.GRAY[90],
  },
  components: {
    Layout: {
      bodyBg: DESIGN_TOKENS.colors.surface.level2,
      headerBg: DESIGN_TOKENS.colors.surface.level1,
      siderBg: DESIGN_TOKENS.colors.surface.level1,
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(0, 0, 0, 0.04)',
      itemHoverBg: 'transparent',
      itemActiveBg: 'rgba(0, 0, 0, 0.04)',
      itemSelectedColor: COLOR.GRAY[10],
      colorText: COLOR.GRAY[10],
      fontSize: FONT.SIZE.SM,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
    },
    Tree: {
      nodeSelectedBg: SEMANTIC_COLOR.CONTAINER.STANDARD.OPACITY_LEVEL1,
      nodeSelectedColor: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
      nodeHoverBg: SEMANTIC_COLOR.CONTAINER.STANDARD.OPACITY_LEVEL1,
      nodeHoverColor: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
      colorPrimary: DESIGN_TOKENS.colors.primary,
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      lineHeight: DESIGN_TOKENS.typography.lineHeightTree,
      padding: DESIGN_TOKENS.spacing.paddingXS,
      borderRadius: DESIGN_TOKENS.spacing.borderRadiusXSmall,
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      directoryNodeSelectedBg: SEMANTIC_COLOR.CONTAINER.STANDARD.OPACITY_LEVEL1,
      directoryNodeSelectedColor: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
    },
    Tabs: {
      colorPrimary: DESIGN_TOKENS.colors.primaryActive,
      colorPrimaryActive: DESIGN_TOKENS.colors.primaryActive,
      colorPrimaryHover: DESIGN_TOKENS.colors.primaryHover,
      inkBarColor: DESIGN_TOKENS.colors.primary,
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
      colorText: COLOR.GRAY[20],
    },
    Button: {
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      borderRadius: DESIGN_TOKENS.spacing.borderRadiusSmall,
      primaryColor: COLOR.GRAY[100],
      fontWeightStrong: FONT.WEIGHT.BOLD,
      boxShadow: 'none',
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      lineWidth: 1,
      controlHeight: 30,
      paddingContentHorizontal: 15,
      colorPrimary: DESIGN_TOKENS.colors.primary,
      colorPrimaryBorder: DESIGN_TOKENS.colors.primary,
      colorPrimaryHover: DESIGN_TOKENS.colors.primaryHover,
      colorPrimaryActive: DESIGN_TOKENS.colors.primaryActive,
      fontSize: DESIGN_TOKENS.typography.fontSize.base,
      lineHeight: DESIGN_TOKENS.typography.lineHeight,
      defaultBg: COLOR.GRAY[100],
      defaultBorderColor: COLOR.GRAY[80],
      defaultColor: COLOR.GRAY[10],
    },
    Typography: {
      fontFamilyCode: `${DESIGN_TOKENS.typography.fontFamily.replace('sans-serif', 'monospace')}`,
    },
    Card: {
      borderRadius: DESIGN_TOKENS.spacing.borderRadius,
      bodyPadding: SPACE[16],
    },
    Input: {
      borderRadius: DESIGN_TOKENS.spacing.borderRadiusSmall,
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      fontSize: DESIGN_TOKENS.typography.fontSize.base,
      lineHeight: DESIGN_TOKENS.typography.lineHeight,
      colorBorder: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL5,
      hoverBorderColor: SEMANTIC_COLOR.ON_CONTAINER.PRIMARY.LEVEL4,
      activeBorderColor: SEMANTIC_COLOR.ON_CONTAINER.PRIMARY.LEVEL4,
    },
    Select: {
      borderRadius: RADIUS[8],
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      colorBorder: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL4,
      colorBgContainer: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL1,
      colorText: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL1,
      controlHeight: 32,
      optionSelectedBg: SEMANTIC_COLOR.CONTAINER.STANDARD.OPACITY_LEVEL1,
      optionSelectedColor: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
      optionSelectedFontWeight: FONT.WEIGHT.REGULAR,
      optionPadding: `${SPACE[8]}px ${SPACE[12]}px`,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
    },
    TreeSelect: {
      nodeSelectedBg: SEMANTIC_COLOR.CONTAINER.STANDARD.OPACITY_LEVEL1,
      nodeSelectedColor: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
      nodeHoverBg: SEMANTIC_COLOR.CONTAINER.STANDARD.OPACITY_LEVEL1,
      nodeHoverColor: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
      colorPrimary: DESIGN_TOKENS.colors.primary,
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      lineHeight: DESIGN_TOKENS.typography.lineHeightTree,
      padding: DESIGN_TOKENS.spacing.paddingXS,
      borderRadius: DESIGN_TOKENS.spacing.borderRadiusXSmall,
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
    },
    Table: {
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      fontSize: DESIGN_TOKENS.typography.fontSize.base,
      lineHeight: DESIGN_TOKENS.typography.lineHeight,
      borderRadius: DESIGN_TOKENS.spacing.borderRadius,
    },
    Tooltip: {
      colorBgSpotlight: DESIGN_TOKENS.colors.surface.level1,
      colorTextLightSolid: DESIGN_TOKENS.colors.onSurface.level1,
      borderRadius: DESIGN_TOKENS.spacing.borderRadius,
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
    },
    Popover: {
      colorBgElevated: DESIGN_TOKENS.colors.surface.level1,
      borderRadius: DESIGN_TOKENS.spacing.borderRadius,
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
    },
    Spin: {
      colorPrimary: DESIGN_TOKENS.colors.primary,
    },
    Switch: {
      colorPrimary: DESIGN_TOKENS.colors.primary,
      colorPrimaryHover: DESIGN_TOKENS.colors.primaryHover,
    },
    Pagination: {
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      itemSize: 32,
      itemSizeSM: 32,
      borderRadius: DESIGN_TOKENS.spacing.borderRadiusSmall,
      colorBorder: COLOR.GRAY[80],
      colorBgContainer: COLOR.GRAY[100],
      colorText: COLOR.GRAY[10],
      colorPrimary: DESIGN_TOKENS.colors.primary,
      colorPrimaryHover: DESIGN_TOKENS.colors.primaryActive,
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
    },
    Checkbox: {
      colorPrimary: SEMANTIC_COLOR.ON_CONTAINER.PRIMARY.LEVEL4,
      colorPrimaryBorder: SEMANTIC_COLOR.ON_CONTAINER.PRIMARY.LEVEL4,
      colorPrimaryHover: SEMANTIC_COLOR.ON_CONTAINER.PRIMARY.LEVEL4,
      colorBgContainer: COLOR.GRAY[100],
      controlInteractiveSize: 14,
      borderRadiusSM: 2,
      lineWidth: 1.5,
    },
    Tag: {
      borderRadiusSM: RADIUS[6],
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      fontSize: FONT.SIZE.SM,
      lineHeight: FONT.LINE_HEIGHT[16] / FONT.SIZE.SM,
      defaultBg: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL2,
      defaultColor: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL1,
    },
    Modal: {
      // @ts-expect-error Modal `contentPadding` missing from ThemeConfig typings (antd)
      contentPadding: `${SPACE[28]}px ${SPACE[32]}px`,
    },
  },
};
